import { ChevronDown, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { runWithFeedback } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { OrganisationCombobox, type OrganisationChoice } from '../components/OrganisationCombobox'
import { listViews, searchRecords } from '../query/api'
import type { ContactRow, Filter, ViewsResponse } from '../query/types'
import { addListMembers } from './api'
import type { MembersAdded } from './types'

type Mode = 'zoeken' | 'weergave' | 'organisatie'

const TITLES: Record<Mode, string> = {
  zoeken: 'Contacten zoeken',
  weergave: 'Uit een weergave',
  organisatie: 'Contactpersonen van een organisatie',
}
const MAX_QUERY_MEMBERS = 5000
const SEARCH_DELAY_MS = 250

const contacts = (count: number): string => (count === 1 ? '1 contact' : `${count} contacten`)

/** The reasons of the contacts that were skipped, each once with its number. */
function groupReasons(skipped: MembersAdded['skipped']): { reason: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const item of skipped) counts.set(item.reason, (counts.get(item.reason) ?? 0) + 1)
  return [...counts].map(([reason, count]) => ({ reason, count }))
}

/** Search contacts and tick several; what is ticked stays while the search text changes. */
function SearchPicker({ picked, onChange }: { picked: ReadonlyMap<string, string>; onChange: (next: Map<string, string>) => void }) {
  const [term, setTerm] = useState('')
  const [hits, setHits] = useState<{ term: string; rows: ContactRow[] | null } | null>(null)
  const trimmed = term.trim()

  useEffect(() => {
    if (trimmed.length < 2) return
    let stale = false
    const timer = setTimeout(() => {
      searchRecords<ContactRow>('contacten', { query: { version: 2, quick: [], groups: [], q: trimmed }, page_size: 8 }).then(
        (page) => {
          if (!stale) setHits({ term: trimmed, rows: page.items })
        },
        () => {
          if (!stale) setHits({ term: trimmed, rows: null })
        },
      )
    }, SEARCH_DELAY_MS)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [trimmed])

  const current = trimmed.length >= 2 && hits?.term === trimmed ? hits : null
  const shown = (current?.rows ?? []).filter((row) => !picked.has(row.person_id))

  function toggle(id: string, name: string) {
    const next = new Map(picked)
    if (!next.delete(id)) next.set(id, name)
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-3">
      <Input type="search" aria-label="Zoek contacten" placeholder="Zoek op naam, e-mail of organisatie…" value={term} onChange={(event) => setTerm(event.target.value)} autoFocus />
      {picked.size > 0 ? (
        <ul aria-label="Gekozen contacten" className="flex flex-wrap gap-1.5">
          {[...picked].map(([id, name]) => (
            <li key={id} className="inline-flex items-center gap-1 rounded-full bg-muted py-0.5 pr-1 pl-3 text-sm">
              {name}
              <Button type="button" variant="ghost" size="icon-xs" aria-label={`${name} weghalen`} onClick={() => toggle(id, name)}>
                <X />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {trimmed.length < 2 ? (
        <p className="text-sm text-muted-foreground">Typ minstens twee tekens.</p>
      ) : current === null ? (
        <p role="status" className="text-sm text-muted-foreground">
          Zoeken…
        </p>
      ) : current.rows === null ? (
        <p role="alert" className="text-sm text-destructive">
          Zoeken mislukt.
        </p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">Geen contacten gevonden.</p>
      ) : (
        <ul className="flex flex-col">
          {shown.map((row) => (
            <li key={row.person_id}>
              <button
                type="button"
                className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-muted max-md:min-h-11"
                onClick={() => toggle(row.person_id, row.full_name)}
              >
                <span className="truncate font-medium">{row.full_name}</span>
                <span className="truncate text-muted-foreground">{row.primary_organisation?.name ?? row.email ?? ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Pick one of the views of Contacten and see how many people it holds before they are added. */
function ViewPicker({ onPick }: { onPick: (pick: { query: Filter; total: number } | null) => void }) {
  const [views, setViews] = useState<ViewsResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const [chosen, setChosen] = useState('')
  const [counted, setCounted] = useState<{ key: string; total: number | null } | null>(null)

  useEffect(() => {
    let stale = false
    listViews('contacten').then(
      (found) => {
        if (!stale) setViews(found)
      },
      () => {
        if (!stale) setFailed(true)
      },
    )
    return () => {
      stale = true
    }
  }, [])

  const all = views ? [...views.system, ...views.views] : []
  const view = all.find((candidate) => candidate.key === chosen) ?? null

  async function choose(key: string) {
    setChosen(key)
    onPick(null)
    setCounted(null)
    const picked = all.find((candidate) => candidate.key === key)
    if (!picked) return
    try {
      const page = await searchRecords<unknown>('contacten', { query: picked.query, page_size: 0 })
      setCounted({ key, total: page.total })
      onPick({ query: picked.query, total: page.total })
    } catch {
      setCounted({ key, total: null })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5 text-sm font-semibold">
        Weergave van Contacten
        <NativeSelect value={chosen} onChange={(event) => void choose(event.target.value)} disabled={views === null}>
          <option value="">{views === null && !failed ? 'Laden…' : 'Kies een weergave'}</option>
          {all.map((candidate) => (
            <option key={candidate.key} value={candidate.key}>
              {candidate.name}
            </option>
          ))}
        </NativeSelect>
      </label>
      {failed ? (
        <p role="alert" className="text-sm text-destructive">
          De weergaven laden mislukt.
        </p>
      ) : null}
      {view && counted?.key === view.key ? (
        counted.total === null ? (
          <p role="alert" className="text-sm text-destructive">
            Tellen mislukt.
          </p>
        ) : counted.total > MAX_QUERY_MEMBERS ? (
          <p role="alert" className="text-sm text-destructive">{`${contacts(counted.total)}: maximaal 5.000 tegelijk. Maak de weergave kleiner.`}</p>
        ) : (
          <p role="status" className="text-sm">{`${contacts(counted.total)} in '${view.name}'.`}</p>
        )
      ) : null}
    </div>
  )
}

/**
 * "Leden toevoegen" on a static list (spec-63 E5, design §4.7): search contacts and tick several, take everyone
 * of a view of Contacten (after seeing how many), or take all current contact persons of an organisation.
 * What is added and what is skipped goes in one toast; the reasons stay behind "details" until the next add.
 */
export function AddMembersMenu({ listId, listName, onAdded }: { listId: string; listName: string; onAdded: () => void }) {
  const [mode, setMode] = useState<Mode | null>(null)
  const [picked, setPicked] = useState<ReadonlyMap<string, string>>(new Map())
  const [viewPick, setViewPick] = useState<{ query: Filter; total: number } | null>(null)
  const [organisation, setOrganisation] = useState<OrganisationChoice | null>(null)
  const [busy, setBusy] = useState(false)
  const [skipped, setSkipped] = useState<MembersAdded['skipped']>([])

  function close() {
    setMode(null)
    setPicked(new Map())
    setViewPick(null)
    setOrganisation(null)
  }

  const ready =
    (mode === 'zoeken' && picked.size > 0) ||
    (mode === 'weergave' && viewPick !== null && viewPick.total > 0 && viewPick.total <= MAX_QUERY_MEMBERS) ||
    (mode === 'organisatie' && organisation?.kind === 'existing')

  async function submit() {
    if (busy || !ready) return
    setBusy(true)
    const result = await runWithFeedback(
      () => {
        if (mode === 'zoeken') return addListMembers(listId, { person_ids: [...picked.keys()] })
        if (mode === 'weergave' && viewPick) return addListMembers(listId, { query: viewPick.query, expected_total: viewPick.total })
        if (organisation?.kind === 'existing') return addListMembers(listId, { organisation_id: organisation.id })
        return Promise.reject(new Error('Niets gekozen.'))
      },
      { error: 'Toevoegen aan de lijst mislukt.' },
    )
    setBusy(false)
    if (!result) return
    setSkipped(result.skipped)
    toast.success(`${result.added} toegevoegd${result.skipped.length > 0 ? ` · ${result.skipped.length} overgeslagen` : ''}`)
    close()
    onAdded()
  }

  const reasons = groupReasons(skipped)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button">
            <Plus />
            Leden toevoegen
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => setMode('zoeken')}>Zoeken</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setMode('weergave')}>Uit een weergave</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setMode('organisatie')}>Alle contactpersonen van een organisatie</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {reasons.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="link" size="sm">
              {`${skipped.length} overgeslagen: details`}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" aria-label="Overgeslagen contacten" className="w-80 p-3">
            <ul className="flex flex-col gap-1 text-sm">
              {reasons.map((item) => (
                <li key={item.reason}>{`${item.count} × ${item.reason}`}</li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
      <Dialog open={mode !== null} onOpenChange={(open) => (open ? undefined : close())}>
        {mode ? (
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>{TITLES[mode]}</DialogTitle>
              <DialogDescription>{`Aan lijst '${listName}'. Wie er al op staat of niet meer bestaat wordt overgeslagen.`}</DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {mode === 'zoeken' ? <SearchPicker picked={picked} onChange={setPicked} /> : null}
              {mode === 'weergave' ? <ViewPicker onPick={setViewPick} /> : null}
              {mode === 'organisatie' ? <OrganisationCombobox value={organisation} onChange={setOrganisation} allowNew={false} autoFocus /> : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Annuleren
              </Button>
              <Button type="button" disabled={!ready || busy} onClick={() => void submit()}>
                {busy
                  ? 'Bezig…'
                  : mode === 'zoeken'
                    ? `${contacts(picked.size)} toevoegen`
                    : mode === 'weergave' && viewPick
                      ? `${contacts(viewPick.total)} toevoegen`
                      : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  )
}
